import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const GOLD = '#d4af37';

interface DeitiesContent {
  title: string;
  description: string;
  principalDeity: string;
  principalDescription: string;
  presidingDeity: string;
  presidingDescription: string;
  otherDeities: string;
  worshipSignificance: string;
}

const DeitiesAboutManager = () => {
  const [content, setContent] = useState<DeitiesContent>({
    title: 'Deities',
    description: 'Learn about the divine forms worshipped in our temple',
    principalDeity: 'Lord Shiva',
    principalDescription: 'The principal deity worshipped in our sacred temple...',
    presidingDeity: 'Consort Goddess',
    presidingDescription: 'The divine consort who complements the principal deity...',
    otherDeities: 'Other celestial beings and saints revered in the temple',
    worshipSignificance: 'Significance of these deities in our spiritual tradition'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'about_content', 'deities'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as DeitiesContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'about_content', 'deities'), content);
      showSnackbar('Deities content saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field: keyof DeitiesContent, value: string) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Deities Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage deities and divine forms content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🙏 Deities Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Principal Deity"
              value={content.principalDeity}
              onChange={(e) => handleChange('principalDeity', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Principal Deity Description"
              value={content.principalDescription}
              onChange={(e) => handleChange('principalDescription', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Presiding Deity"
              value={content.presidingDeity}
              onChange={(e) => handleChange('presidingDeity', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Presiding Deity Description"
              value={content.presidingDescription}
              onChange={(e) => handleChange('presidingDescription', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Other Deities"
              value={content.otherDeities}
              onChange={(e) => handleChange('otherDeities', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Worship Significance"
              value={content.worshipSignificance}
              onChange={(e) => handleChange('worshipSignificance', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeitiesAboutManager;
